import {
  EMcpServerSuggestionAuth,
  EMcpServerSuggestionSource,
  type IIntegrationConnectPromptMcpServer,
} from "../types/chat";
import {
  EMcpAuthMode,
  EMcpConnectMode,
  EMcpTransport,
} from "../types/integration";
import type {
  IBuildMcpConnectPrefillProps,
  IMcpConnectPrefill,
  TMcpServerAuthLabelKey,
  TMcpServerAuthLabelMap,
  TMcpServerSourceLabelKey,
  TMcpServerSourceLabelMap,
} from "./mcp-server-connect-prompt.types";

// NOTE: NSAI-8152 — helpers behind the chat card that offers ONE MCP server
// ("Atlassian Rovo — Remote MCP server · Needs setup — Review and add"). Shared
// so web and mobile describe the server and prefill the connect form the same
// way; each app only draws its own card.

const MCP_TRANSPORT_VALUES = new Set<string>(Object.values(EMcpTransport));
const MCP_AUTH_MODE_VALUES = new Set<string>(Object.values(EMcpAuthMode));

const isMcpTransport = (value: string | undefined): value is EMcpTransport =>
  value !== undefined && MCP_TRANSPORT_VALUES.has(value);

const isMcpAuthMode = (value: string | undefined): value is EMcpAuthMode =>
  value !== undefined && MCP_AUTH_MODE_VALUES.has(value);

// NOTE: "Runs at mcp.atlassian.com" — the host is what tells a user where their
// data goes, so it is the one part of the URL the card always shows. A value
// that does not parse is shown as stored rather than hidden.
export const getMcpServerHost = (url: string): string => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

// NOTE: The form the card opens. A card stored before an enum value existed
// (or edited by hand in the database) falls back to the form's own defaults —
// auto-detected transport and bearer — rather than to an invalid select value.
export const buildMcpConnectPrefill = ({
  mcpServer,
}: IBuildMcpConnectPrefillProps): IMcpConnectPrefill => ({
  mode:
    mcpServer.source === EMcpServerSuggestionSource.ORGANIZATION
      ? EMcpConnectMode.ORGANIZATION
      : EMcpConnectMode.CUSTOM,
  title: mcpServer.title,
  url: mcpServer.url,
  transport: isMcpTransport(mcpServer.transport)
    ? mcpServer.transport
    : EMcpTransport.AUTO,
  authMode: isMcpAuthMode(mcpServer.authMode)
    ? mcpServer.authMode
    : EMcpAuthMode.BEARER,
  authHeaderName: mcpServer.authHeaderName ?? "",
});

const MAP_MCP_SERVER_AUTH_TO_LABEL_KEY: TMcpServerAuthLabelMap = {
  [EMcpServerSuggestionAuth.OAUTH]: "integration_connect.mcp_server.auth.oauth",
  [EMcpServerSuggestionAuth.API_KEY]:
    "integration_connect.mcp_server.auth.api_key",
  [EMcpServerSuggestionAuth.NONE]: "integration_connect.mcp_server.auth.none",
  [EMcpServerSuggestionAuth.ORGANIZATION]:
    "integration_connect.mcp_server.auth.organization",
  [EMcpServerSuggestionAuth.UNKNOWN]:
    "integration_connect.mcp_server.auth.unknown",
};

const MAP_MCP_SERVER_SOURCE_TO_LABEL_KEY: TMcpServerSourceLabelMap = {
  [EMcpServerSuggestionSource.ORGANIZATION]:
    "integration_connect.mcp_server.source.organization",
  [EMcpServerSuggestionSource.KNOWN]:
    "integration_connect.mcp_server.source.known",
  [EMcpServerSuggestionSource.USER]:
    "integration_connect.mcp_server.source.user",
};

// NOTE: The "Connection" row. An OAuth server that will not register NodeShift
// by itself says so up front, so the user knows to have a client id ready.
export const getMcpServerAuthLabelKey = (
  mcpServer: IIntegrationConnectPromptMcpServer,
): TMcpServerAuthLabelKey =>
  mcpServer.auth === EMcpServerSuggestionAuth.OAUTH &&
  mcpServer.isOAuthClientRequired === true
    ? "integration_connect.mcp_server.auth.oauth_client"
    : (MAP_MCP_SERVER_AUTH_TO_LABEL_KEY[mcpServer.auth] ??
      "integration_connect.mcp_server.auth.unknown");

export const getMcpServerSourceLabelKey = (
  mcpServer: IIntegrationConnectPromptMcpServer,
): TMcpServerSourceLabelKey =>
  // NOTE: Never fall back to "Address you entered": where the address came
  // from is the one trust statement on the card, so an unrecognised source
  // (an older client reading a newer row) says only that the assistant
  // suggested it.
  MAP_MCP_SERVER_SOURCE_TO_LABEL_KEY[mcpServer.source] ??
  "integration_connect.mcp_server.source.unknown";
